import React from 'react';
import { isNullOrUndefined, isNullOrUndefinedOrEmpty, TimeSpan, TimestampRange, UTC } from 'rewire-common';
import { defaultEquals, Observe } from 'rewire-core';
import { EditorType, CheckField, TextField, Form, IFormContext, arraySearch } from 'rewire-ui';
import {
  ArchivedScopeId,
  DeepPartial,
  DeletedScopeId,
  FieldFilter,
  FilterComparative,
  IScheduleRuleContext,
  ITemplate,
  ITimecard,
  IUdf,
  IView,
  lookupKeyFromUdfDocumentType,
  ReasonCategoriesList,
  ResponsesList,
  ScheduleSort,
  SortInput,
  UdfType,
  ViewInsertInput,
  ViewUpdateInput,
} from '../DataTypes';
import DataMutations from '../graphql/Mutations';
import DataQueries from '../graphql/Queries';
import mainPageModel from '../main-navigation/MainPageModel';
import udfsSubscriptionService from '../services/UdfsSubscriptionService';
import {
  defaultGetFormPopoverData,
  getCreateFormPopoverEditorFn,
  IFormPopoverData,
  IFormPopoverEditorComponentWrapperProps,
} from '../ui/FormPopoverEditor';
import { notifications } from '../ui/Notifications';
import PrioritySortMenuModel, { IPrioritySortMenuItem } from '../ui/PrioritySortMenuModel';
import CustomFilterDialogModel from '../utility/CustomFilterDialogModel';
import { IPropertyMapField, isMultiValueEditorType } from '../utility/CustomFilterModel';
import { timecardTemplateToTimecard } from '../utility/DataConverters';
import { Q } from '../utility/q';
import { boolSearcher, getNameSearcher } from '../utility/Searchers';
import { capitalize } from '../utility/StringUtils';
import { makeStyles, Theme } from '../utility/styles';
import AutoScheduleQueryMapper, { AutoSchedulePropertyMap, ExpressionFilterFn } from './AutoScheduleQueryMapper';
import ScheduleRuleContextSearch from './ScheduleRuleContextSearch';
import ScheduleRuleCustomFilterModel from './ScheduleRuleCustomFilterModel';
import SchedulingCandidateEmployeeGridModel from './SchedulingCandidateEmployeeGridModel';

export enum FilterSpecialValueKey {
  SelectedTimecardsValue = 'selectedTimecardsValue',
  SelectedTimecardsOccupation = 'selectedTimecardsOccupation',
}

export interface IFilterSpecialValue {
  id: FilterSpecialValueKey;
  title: string;
  display: string;
}

export const filterSpecialValuesMap: Record<FilterSpecialValueKey, IFilterSpecialValue> = {
  selectedTimecardsValue: {
    id: FilterSpecialValueKey.SelectedTimecardsValue,
    title: 'Use Value of Selected Shift',
    display: 'Use Value of Selected Shift',
  },
  selectedTimecardsOccupation: {
    id: FilterSpecialValueKey.SelectedTimecardsOccupation,
    title: 'Use Occupation of Selected Shift',
    display: 'Use Occupation of Selected Shift',
  },
};

export function isFilterSpecialValue(value: any) {
  return Object.keys(filterSpecialValuesMap).some((key) => {
    if (Array.isArray(value)) {
      return key === value[0]?.id;
    }
    return key === value?.id;
  });
}

export function getFilterSpecialValue(value: any): IFilterSpecialValue | undefined {
  const sVal = Object.values(filterSpecialValuesMap).find((specialValue: IFilterSpecialValue) => {
    if (Array.isArray(value)) {
      return specialValue.id === value[0]?.id;
    }
    return specialValue.id === value?.id;
  });

  return sVal ? { ...sVal } : undefined;
}

const useStyle = makeStyles((_theme: Theme) => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
  },
  textFormControlRoot: {
    width: '100%',
  },
  specialValuesCheckboxesContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '25px',
    marginTop: '10px',
  },
}));

const getFormPopoverEditorComponentWrapper = (specialValueKeys?: string[]) => (props: IFormPopoverEditorComponentWrapperProps) => {
  const { editorType, EditorComponent, ...restProps } = props;
  const styles = useStyle();
  const field = restProps.field;
  const label = field?.label;
  const propsOnValueChange = restProps.onValueChange;

  return (
    <Observe
      render={() => {
        const value = field?.value;
        const specialValue = getFilterSpecialValue(value);
        return (
          <div className={styles.root}>
            {!specialValue ? (
              <EditorComponent {...restProps} />
            ) : (
              <TextField
                classes={{ formControlRoot: styles.textFormControlRoot }}
                variant={'outlined'}
                label={label}
                value={specialValue?.display}
                onValueChange={() => {}}
                disabled={true}
              />
            )}
            {specialValueKeys?.length ? (
              <div className={styles.specialValuesCheckboxesContainer}>
                {specialValueKeys.map((sKey: FilterSpecialValueKey) => {
                  const sValue = filterSpecialValuesMap[sKey];
                  const sOnValueChange = (checkVal: boolean) => {
                    if (checkVal) {
                      const newFieldValue = isMultiValueEditorType(editorType) ? [{ id: sKey }] : { id: sKey };
                      propsOnValueChange(newFieldValue);
                    } else {
                      propsOnValueChange(undefined);
                    }
                  };
                  return (
                    <CheckField key={sKey} label={sValue?.title} value={specialValue?.id === sKey} onValueChange={sOnValueChange} />
                  );
                })}
              </div>
            ) : null}
          </div>
        );
      }}
    />
  );
};

function getFormPopoverData(value: any, editorType: EditorType, valueType: UdfType, editorOptions: any): IFormPopoverData {
  const specialValue = getFilterSpecialValue(value);
  return specialValue
    ? { title: specialValue.title, display: specialValue.display }
    : defaultGetFormPopoverData(value, editorType, valueType, editorOptions);
}

const inactiveScopes = [DeletedScopeId, ArchivedScopeId];

function employeeScopeFilterMapToForm(fieldFilter: FieldFilter): FieldFilter {
  const _fieldFilter = { ...fieldFilter };

  if (!isFilterSpecialValue(_fieldFilter[FilterComparative.In]) && !isFilterSpecialValue(_fieldFilter[FilterComparative.NotIn])) {
    if (defaultEquals(_fieldFilter[FilterComparative.In], inactiveScopes)) {
      _fieldFilter[FilterComparative.Equals] = false;
    } else if (defaultEquals(_fieldFilter[FilterComparative.NotIn], inactiveScopes)) {
      _fieldFilter[FilterComparative.Equals] = true;
    }
    delete _fieldFilter[FilterComparative.In];
    delete _fieldFilter[FilterComparative.NotIn];
  }

  return _fieldFilter;
}

function employeeScopeFilterMapToSave(fieldFilter: FieldFilter, value: any, _comparative: FilterComparative) {
  if (isFilterSpecialValue(value)) {
    return fieldFilter;
  }

  const _fieldFilter = { ...fieldFilter };

  if (value) {
    _fieldFilter[FilterComparative.NotIn] = inactiveScopes;
    delete _fieldFilter[FilterComparative.In];
  } else {
    _fieldFilter[FilterComparative.In] = inactiveScopes;
    delete _fieldFilter[FilterComparative.NotIn];
  }

  delete _fieldFilter[FilterComparative.Equals];

  return _fieldFilter;
}

function timecardEffectiveMapToQuery(fieldFilter: FieldFilter) {
  const _fieldFilter = { ...fieldFilter };
  let comparative: FilterComparative | undefined;
  let value: any;
  if (_fieldFilter[FilterComparative.Intersects]) {
    comparative = FilterComparative.Intersects;
    value = _fieldFilter[FilterComparative.Intersects];
  } else if (_fieldFilter[FilterComparative.NotIntersects]) {
    comparative = FilterComparative.NotIntersects;
    value = _fieldFilter[FilterComparative.NotIntersects];
  }

  if (value && comparative) {
    const isValueLowerSpecialValue = isFilterSpecialValue(value.lower);
    const isValueUpperSpecialValue = isFilterSpecialValue(value.upper);
    let newLower = value.lower;
    let newUpper = value.upper;
    if (!isNullOrUndefinedOrEmpty(value.lower) && !isValueLowerSpecialValue) {
      newLower = `$context.earnedDate.add(${value.lower}, ${TimeSpan.hours})`;
    }
    if (!isNullOrUndefinedOrEmpty(value.upper) && !isValueUpperSpecialValue) {
      newUpper = `$context.earnedDate.add(${value.upper}, ${TimeSpan.hours})`;
    }
    _fieldFilter[comparative] = { ...value, lower: newLower, upper: newUpper };
  }

  return _fieldFilter;
}

function minimumTimeOffCustomFilter(
  _addIncludeFilter: FilterFn,
  _addIncludeExpressionFilter: ExpressionFilterFn,
  addExcludeFilter: FilterFn,
  fieldFilter: FieldFilter,
) {
  const _fieldFilter = { ...fieldFilter };
  let comparative: FilterComparative | undefined;
  let value: any;
  if (_fieldFilter[FilterComparative.Equals]) {
    comparative = FilterComparative.Equals;
    value = _fieldFilter[FilterComparative.Equals];
  }

  if (value && comparative) {
    addExcludeFilter({
      field: 'effective',
      intersects: {
        lowerField: 'effective.start',
        lower: `$context.effective.start.add(${-value}, ${TimeSpan.hours})`,
        upperField: 'effective.end',
        upper: `$context.effective.end.add(${value}, ${TimeSpan.hours})`,
      },
    });
  }
}

function hasApprovedTimeoffRequest(
  _addIncludeFilter: FilterFn,
  _addExpressionFilter: ExpressionFilterFn,
  addExcludeFilter: FilterFn,
  _fieldFilter: FieldFilter,
) {
  const value = Q<any>().bool((b) =>
    b.must((m) => [
      {
        range: {
          'effective.start': { lte: '$context.earnedDate' },
        },
      },
      {
        range: {
          'effective.end': { gte: '$context.earnedDate' },
        },
      },

      m.term('status', 'approved'),
    ]),
  );

  addExcludeFilter({
    field: 'effective',
    raw: {
      value,
    },
  });
}

function hasIndicatedUnavailableCustomFilter(
  _addIncludeFilter: FilterFn,
  _addExpressionFilter: ExpressionFilterFn,
  addExcludeFilter: FilterFn,
  fieldFilter: FieldFilter,
) {
  const inValues = Array.from((fieldFilter as any)['in'] ?? ['no']);
  const value = Q<any>().bool((b) =>
    b.must((_f) => [
      {
        term: {
          effective: '$context.earnedDate',
        },
      },
      {
        terms: {
          type: inValues,
        },
      },
      {
        term: {
          'occupation.id': '$context.occupationId',
        },
      },
      {
        term: {
          'template.id': '$context.templateId',
        },
      },
    ]),
  );

  addExcludeFilter({
    field: 'effective',
    raw: {
      value,
    },
  });
}

function adjacentToTheStartCustomFilter(
  addIncludeFilter: FilterFn,
  _addExpressionFilter: ExpressionFilterFn,
  addExcludeFilter: FilterFn,
  _fieldFilter: FieldFilter,
) {
  addIncludeFilter({
    field: 'effective.end',
    eq: '$context.effective.start',
  });

  addExcludeFilter({
    field: 'effective',
    intersects: {
      lowerField: 'effective.start',
      lower: '$context.effective.start',
      upperField: 'effective.end',
      upper: '$context.effective.end',
    },
  });
}

function adjacentToTheEndCustomFilter(
  addIncludeFilter: FilterFn,
  _addExpressionFilter: ExpressionFilterFn,
  addExcludeFilter: FilterFn,
  _fieldFilter: FieldFilter,
) {
  addIncludeFilter({
    field: 'effective.start',
    eq: '$context.effective.end',
  });

  addExcludeFilter({
    field: 'effective',
    intersects: {
      lowerField: 'effective.start',
      lower: '$context.effective.start',
      upperField: 'effective.end',
      upper: '$context.effective.end',
    },
  });
}

function _enhancePropertyMapField(propertyMapField: ISchedulingPropertyMapField): void {
  if (propertyMapField.category === 'employee') {
    if (propertyMapField.internalField === 'employee.fields.qualifications:r.id') {
      propertyMapField.specialValueKeys = [FilterSpecialValueKey.SelectedTimecardsOccupation];
    }
  } else {
    propertyMapField.specialValueKeys = [FilterSpecialValueKey.SelectedTimecardsValue];
  }
  propertyMapField.customEditorComponentFn = getCreateFormPopoverEditorFn(
    getFormPopoverData,
    getFormPopoverEditorComponentWrapper(propertyMapField.specialValueKeys),
  );
}

function enhancePropertyMapField(propertyMapField: ISchedulingPropertyMapField): void {
  _enhancePropertyMapField(propertyMapField);
}

function enhanceUdfPropertyMapField(propertyMapField: ISchedulingPropertyMapField, udf: IUdf): void {
  const field = propertyMapField.field;
  propertyMapField.category = lookupKeyFromUdfDocumentType(udf.documentType) as string;
  const _isExternalUdf = isExternalUdfCategory(propertyMapField.category);
  propertyMapField.internalField = _isExternalUdf ? field : field.substring(field.indexOf('.') + 1);
  propertyMapField.index = propertyMapField.category === 'timecard' ? 'timecards' : 'jobs';
  propertyMapField.group = propertyMapField.category === 'timecard' ? 'Shift' : 'Job';
  if (_isExternalUdf) {
    propertyMapField.displayName = `${capitalize(propertyMapField.category)} ${udf.displayName}`;
  }
  _enhancePropertyMapField(propertyMapField);
}

function propertyMapFieldsMapper(propertyMapFields: ISchedulingPropertyMapField[]) {
  const newPropertyMapFields = propertyMapFields.slice();
  // duplicate all employee and group property map fields so that they can be both in the timecards index filters and jobs index filters.
  for (const pmf of propertyMapFields) {
    if (pmf.category === 'employee' || pmf.category === 'group') {
      newPropertyMapFields.push(
        Object.assign({}, pmf, {
          field: `timecard.${pmf.field}`,
          group: 'Shift',
          index: 'timecards',
        }),
      );
    }
  }
  return newPropertyMapFields;
}

function isExternalUdfCategory(category: string) {
  return category !== 'timecard' && category !== 'job';
}

export type FilterFn = (fieldFilter: FieldFilter) => void;

export interface ISchedulingPropertyMapField extends IPropertyMapField {
  internalField: string;
  category: string;
  index: string;
  specialValueKeys?: string[];
  mapToQuery?: (fieldFilter: FieldFilter) => FieldFilter;
  customFilter?: (
    addIncludeFilter: FilterFn,
    addExpressionFilter: ExpressionFilterFn,
    addExcludeFilter: FilterFn,
    fieldFilter: FieldFilter,
  ) => void;
}

const baseSchedulingPropertyMapFields: ISchedulingPropertyMapField[] = [
  {
    field: 'timecard.area.id',
    internalField: 'area.id',
    group: 'Shift',
    category: 'timecard',
    index: 'timecards',
    displayName: 'Area',
    valueType: 'reference',
    editorType: 'auto-complete',
    editorOptions: getNameSearcher('areas'),
  },
  {
    field: 'timecard.group.id',
    internalField: 'group.id',
    group: 'Shift',
    category: 'timecard',
    index: 'timecards',
    displayName: 'Group',
    valueType: 'reference',
    editorType: 'auto-complete',
    editorOptions: getNameSearcher('groups'),
  },
  {
    field: 'timecard.occupation.id',
    internalField: 'occupation.id',
    group: 'Shift',
    category: 'timecard',
    index: 'timecards',
    displayName: 'Occupation',
    valueType: 'reference',
    editorType: 'auto-complete',
    editorOptions: getNameSearcher('occupations'),
  },
  {
    field: 'timecard.job.generator.id',
    internalField: 'generator.id',
    group: 'Shift',
    category: 'timecard',
    index: 'timecards',
    displayName: 'Shift Pattern',
    valueType: 'reference',
    editorType: 'auto-complete',
    editorOptions: getNameSearcher('generators'),
  },
  {
    field: 'timecard.template.id',
    internalField: 'template.id',
    group: 'Shift',
    category: 'timecard',
    index: 'timecards',
    displayName: 'Shift Type',
    valueType: 'reference',
    editorType: 'auto-complete',
    editorOptions: getNameSearcher('templates'),
  },
  {
    field: 'timecard.reason.id',
    internalField: 'reason.id',
    group: 'Shift',
    category: 'timecard',
    index: 'timecards',
    displayName: 'Reason',
    valueType: 'reference',
    editorType: 'auto-complete',
    editorOptions: getNameSearcher('reasons'),
  },
  {
    field: 'timecard.reason.category',
    internalField: 'reason.category',
    group: 'Shift',
    category: 'timecard',
    index: 'timecards',
    displayName: 'Reason Category',
    valueType: 'text',
    editorType: 'auto-complete',
    editorOptions: arraySearch<string>(ReasonCategoriesList as unknown as string[]),
  },
  {
    field: 'timecard.effective',
    internalField: 'effective',
    group: 'Shift',
    category: 'timecard',
    index: 'timecards',
    displayName: 'Effective',
    valueType: 'text',
    editorType: 'time',
    editorOptions: undefined,
    comparatives: [FilterComparative.Intersects, FilterComparative.NotIntersects],
    intersectsFieldNames: { lowerField: 'effective.start', upperField: 'effective.end' },
    mapToQuery: timecardEffectiveMapToQuery,
  },
  {
    field: 'minimum.timeoff',
    internalField: 'effective',
    group: 'Shift',
    category: 'timecard',
    index: 'timecards',
    displayName: 'Minimum Time Off',
    valueType: 'float',
    editorType: 'number',
    editorOptions: undefined,
    comparatives: [FilterComparative.Equals],
    customFilter: minimumTimeOffCustomFilter,
  },
  {
    field: 'approved.timeoff',
    internalField: 'effective',
    group: 'Shift',
    category: 'timeoff-requests',
    index: 'timeoff-requests',
    displayName: 'Has Approved Timeoff Request',
    comparatives: undefined,
    valueType: 'none' as any,
    editorType: 'none',
    editorOptions: undefined,
    customFilter: hasApprovedTimeoffRequest,
  },
  {
    field: 'no.response',
    internalField: 'type',
    group: 'Shift',
    category: 'responses',
    index: 'responses',
    displayName: 'Indicated they are unavailable',
    comparatives: [FilterComparative.In],
    valueType: 'text' as any,
    editorType: 'auto-complete',
    editorOptions: arraySearch<string>(ResponsesList as unknown as string[]),
    customFilter: hasIndicatedUnavailableCustomFilter,
  },
  {
    field: 'adjacent.start',
    internalField: 'effective',
    group: 'Shift',
    category: 'timecard',
    index: 'timecards',
    displayName: 'Adjacent to the start',
    comparatives: undefined,
    valueType: 'none' as any,
    editorType: 'none',
    editorOptions: undefined,
    customFilter: adjacentToTheStartCustomFilter,
  },
  {
    field: 'adjacent.end',
    internalField: 'effective',
    group: 'Shift',
    category: 'timecard',
    comparatives: undefined,
    index: 'timecards',
    displayName: 'Adjacent to the end',
    valueType: 'none' as any,
    editorType: 'none',
    editorOptions: undefined,
    customFilter: adjacentToTheEndCustomFilter,
  },
  {
    field: 'prevtour.employees',
    internalField: 'effective',
    group: 'Shift',
    category: 'timecard',
    comparatives: undefined,
    index: 'timecards',
    displayName: 'Employees on Previous Tour',
    valueType: 'none' as any,
    editorType: 'none',
    editorOptions: undefined,
    customFilter: (_addIncludeFilter: FilterFn, addExpressionFilter: ExpressionFilterFn) => {
      addExpressionFilter('scheduledEmployeesOnPreviousTour()');
    },
  },
  {
    field: 'timecard.units',
    internalField: 'units',
    group: 'Shift',
    category: 'timecard',
    index: 'timecards',
    displayName: 'Units',
    valueType: 'float',
    editorType: 'number',
    editorOptions: { decimals: 4 },
  },
  {
    field: 'timecard.earnedDate',
    internalField: 'earnedDate',
    group: 'Shift',
    category: 'timecard',
    index: 'timecards',
    displayName: 'Earned Date',
    valueType: 'date',
    editorType: 'date',
    editorOptions: undefined,
  },
  {
    field: 'timecard.job.position',
    internalField: 'job.position',
    group: 'Shift',
    category: 'timecard',
    index: 'timecards',
    displayName: 'Position',
    valueType: 'float',
    editorType: 'number',
    editorOptions: { decimals: 4 },
  },
  {
    field: 'area.id',
    internalField: 'area.id',
    group: 'Job',
    category: 'job',
    index: 'jobs',
    displayName: 'Area',
    valueType: 'reference',
    editorType: 'auto-complete',
    editorOptions: getNameSearcher('areas'),
  },
  {
    field: 'group.id',
    internalField: 'group.id',
    group: 'Job',
    category: 'job',
    index: 'jobs',
    displayName: 'Group',
    valueType: 'reference',
    editorType: 'auto-complete',
    editorOptions: getNameSearcher('groups'),
  },
  {
    field: 'occupation.id',
    internalField: 'occupation.id',
    group: 'Job',
    category: 'job',
    index: 'jobs',
    displayName: 'Occupation',
    valueType: 'reference',
    editorType: 'auto-complete',
    editorOptions: getNameSearcher('occupations'),
  },
  {
    field: 'generator.id',
    internalField: 'generator.id',
    group: 'Job',
    category: 'job',
    index: 'jobs',
    displayName: 'Shift Pattern',
    valueType: 'reference',
    editorType: 'auto-complete',
    editorOptions: getNameSearcher('generators'),
  },
  {
    field: 'effective.start',
    internalField: 'effective.start',
    group: 'Job',
    category: 'job',
    index: 'jobs',
    displayName: 'Effective Start',
    valueType: 'date',
    editorType: 'date',
    editorOptions: undefined,
  },
  {
    field: 'effective.end',
    internalField: 'effective.end',
    group: 'Job',
    category: 'job',
    index: 'jobs',
    displayName: 'Effective End',
    valueType: 'date',
    editorType: 'date',
    editorOptions: undefined,
  },
  {
    field: 'position',
    internalField: 'position',
    group: 'Job',
    category: 'job',
    index: 'jobs',
    displayName: 'Position',
    valueType: 'float',
    editorType: 'number',
    editorOptions: { decimals: 4 },
  },
  {
    field: 'employee.id',
    internalField: 'employee.id',
    group: 'Job',
    category: 'employee',
    index: 'jobs',
    displayName: 'Employee',
    valueType: 'reference',
    editorType: 'auto-complete',
    editorOptions: getNameSearcher('employees'),
  },
  {
    field: 'employee.tags',
    internalField: 'employee.tags',
    group: 'Job',
    category: 'employee',
    index: 'jobs',
    displayName: 'Employee Tags',
    valueType: 'text',
    editorType: 'text',
    editorOptions: undefined,
  },
  {
    field: 'employee.email',
    internalField: 'employee.email',
    group: 'Job',
    category: 'employee',
    index: 'jobs',
    displayName: 'Employee Email',
    valueType: 'text',
    editorType: 'text',
    editorOptions: undefined,
  },
  {
    field: 'employee.scopeId',
    internalField: 'employee.scopeId',
    group: 'Job',
    category: 'employee',
    index: 'jobs',
    displayName: 'Employee Active',
    valueType: 'boolean',
    editorType: 'select',
    editorOptions: { ...boolSearcher },
    mapToForm: employeeScopeFilterMapToForm,
    mapToSave: employeeScopeFilterMapToSave,
  },
  {
    field: 'employee.phone.home',
    internalField: 'employee.phone.home',
    group: 'Job',
    category: 'employee',
    index: 'jobs',
    displayName: 'Employee Phone - Home',
    valueType: 'text',
    editorType: 'phone',
    editorOptions: undefined,
    comparatives: [FilterComparative.Equals, FilterComparative.NotEquals],
  },
  {
    field: 'employee.phone.mobile',
    internalField: 'employee.phone.mobile',
    group: 'Job',
    category: 'employee',
    index: 'jobs',
    displayName: 'Employee Phone - Mobile',
    valueType: 'text',
    editorType: 'phone',
    editorOptions: undefined,
    comparatives: [FilterComparative.Equals, FilterComparative.NotEquals],
  },
  {
    field: 'employee.phone.work',
    internalField: 'employee.phone.work',
    group: 'Job',
    category: 'employee',
    index: 'jobs',
    displayName: 'Employee Phone - Work',
    valueType: 'text',
    editorType: 'phone',
    editorOptions: undefined,
    comparatives: [FilterComparative.Equals, FilterComparative.NotEquals],
  },
  {
    field: 'employee.phone.fax',
    internalField: 'employee.phone.fax',
    group: 'Job',
    category: 'employee',
    index: 'jobs',
    displayName: 'Employee Phone - Fax',
    valueType: 'text',
    editorType: 'phone',
    editorOptions: undefined,
    comparatives: [FilterComparative.Equals, FilterComparative.NotEquals],
  },
  {
    field: 'employee.phone.workFax',
    internalField: 'employee.phone.workFax',
    group: 'Job',
    category: 'employee',
    index: 'jobs',
    displayName: 'Employee Phone - Work Fax',
    valueType: 'text',
    editorType: 'phone',
    editorOptions: undefined,
    comparatives: [FilterComparative.Equals, FilterComparative.NotEquals],
  },
];

for (const pmf of baseSchedulingPropertyMapFields) {
  enhancePropertyMapField(pmf);
}

export function getScheduleRuleContext(timecards?: ITimecard[]) {
  const numTimecards = timecards?.length ?? 0;
  if (numTimecards <= 0) {
    return undefined;
  }
  if (numTimecards === 1) {
    return mapTimecardToRuleContext(timecards![0]);
  }

  // combine multiple timecards
  const timecardForContext = Object.assign({}, timecards![0]);
  let minStart = timecardForContext.effective?.start;
  let maxEnd = timecardForContext.effective?.end;
  for (let i = 1; i < timecards!.length; i++) {
    const timecard = timecards![i];
    if (!isNullOrUndefined(timecard.effective?.start) && (isNullOrUndefined(minStart) || timecard.effective!.start < minStart)) {
      minStart = timecard.effective!.start;
    }
    if (!isNullOrUndefined(timecard.effective?.end) && (isNullOrUndefined(maxEnd) || timecard.effective!.end > maxEnd)) {
      maxEnd = timecard.effective!.end;
    }
    timecardForContext.units! += timecard.units!;
  }

  if (!isNullOrUndefined(minStart) && !isNullOrUndefined(maxEnd)) {
    timecardForContext.effective = new TimestampRange(minStart, maxEnd);
  }

  return mapTimecardToRuleContext(timecardForContext);
}

function mapTimecardToRuleContext(timecard: ITimecard) {
  const ruleContext = {
    jobId: timecard.job?.id,
    earnedDate: timecard.earnedDate,
    effective: timecard.effective,
    reasonId: timecard.reason?.id,
    areaId: timecard.group?.area?.id,
    groupId: timecard.group?.id,
    occupationId: timecard.occupation?.id,
    templateId: timecard.template?.id,
    generatorId: timecard.job?.generator?.id,
    units: timecard.units,
    position: timecard.job?.position,
    jobFields: timecard.job?.fields,
    groupFields: timecard.group?.fields,
    timecardFields: timecard.fields,
  } as IScheduleRuleContext;
  return ruleContext;
}

function getScheduleRuleSortsForQuery(sorts?: ScheduleSort): ScheduleSort | undefined {
  if (!sorts) return;
  const sortsForQuery = { ...sorts } as ScheduleSort;
  if (sortsForQuery?.custom) {
    const customSorts = sortsForQuery.custom.map((customSort: SortInput) => ({ ...customSort }) as SortInput);
    for (const sortInput of customSorts) {
      const documentType = udfsSubscriptionService.subscriptionModel.getUdfDocumentType(sortInput.field);
      if (!documentType) continue;
      const lookupType = lookupKeyFromUdfDocumentType(documentType);
      if (!lookupType) continue;
      sortInput.field = `${lookupType}.fields.${sortInput.field}`;
    }
    sortsForQuery.custom = customSorts;
  }
  return sortsForQuery;
}

export function getScheduleRuleQuery(propertyMap: AutoSchedulePropertyMap, filters?: ISchedulingRuleFilters, sorts?: ScheduleSort) {
  const sortsForQuery = getScheduleRuleSortsForQuery(sorts);
  return AutoScheduleQueryMapper.getQuery(propertyMap, { filters: filters, sorts: sortsForQuery });
}

export interface ISchedulingRuleFilters {
  custom?: FieldFilter[];
}

export interface ISchedulingRuleOptions {
  filters?: ISchedulingRuleFilters;
  sorts?: ScheduleSort;
}

export interface ISchedulingRuleDialogData {
  id?: string;
  name?: string;
  options?: ISchedulingRuleOptions;
  query?: string;
}

interface IScheduleRuleDialogModelState {
  scheduleRuleId?: string;
  filters: ISchedulingRuleFilters;
  scheduleRuleContext?: IScheduleRuleContext;
  searchScheduleRuleContext?: IScheduleRuleContext;
  scheduleRuleQuery?: string;
  disableDate: boolean;
  scheduleRuleContextSearchComponent?: () => JSX.Element;
  onSave?: (scheduleRule: ISchedulingRuleDialogData) => boolean;
  onDelete?: (scheduleRule: ISchedulingRuleDialogData) => boolean;
}

export class ScheduleRuleDialogModel extends CustomFilterDialogModel<IScheduleRuleDialogModelState> {
  sortModel: PrioritySortMenuModel;
  schedulingGridModel: SchedulingCandidateEmployeeGridModel;

  constructor() {
    super(
      'Scheduling Rule',
      baseSchedulingPropertyMapFields,
      ['employees', 'timecards', 'jobs', 'groups'],
      enhanceUdfPropertyMapField,
      propertyMapFieldsMapper,
    );
    this.state.filters = {} as ISchedulingRuleFilters;
    this.state.scheduleRuleContext = undefined;
    this.state.searchScheduleRuleContext = undefined;
    this.state.scheduleRuleQuery = undefined;
    this.state.scheduleRuleContextSearchComponent = undefined;
    this.state.disableDate = false;
    this.sortModel = new PrioritySortMenuModel(() => {}, this.getPrioritySortItemsFn());
    this.schedulingGridModel = new SchedulingCandidateEmployeeGridModel(this.onGridDateChange);
    this.action('save', this.submit, {
      variant: 'contained',
      color: 'primary',
      disabled: () => this.hasErrors() || !this.hasChanges(),
      type: 'submit',
    }).action('reload schedule', async () => await this.loadScheduleRuleCandidateList(true, true), {
      variant: 'contained',
      disabled: () => this.hasCustomFilterErrors(),
    });
  }

  protected _createForm(): Form {
    return Form.create(
      (ctx: IFormContext) => ({
        scheduleRuleName: ctx.string().label('Schedule Rule Name').validators('required').placeholder('enter schedule rule name'),
      }),
      {},
      { variant: 'outlined', initialValuesValidationMode: 'none' },
    );
  }

  get CustomFilterModelClass() {
    return ScheduleRuleCustomFilterModel;
  }

  get scheduleRuleId(): string | undefined {
    return this.state.scheduleRuleId;
  }

  get filters(): ISchedulingRuleFilters {
    return this.state.filters;
  }

  get hasFilters(): boolean {
    return !!this.filters?.custom?.length;
  }

  get sorts(): ScheduleSort {
    return this.sortModel.sorts;
  }

  get hasSorts(): boolean {
    return this.sortModel.hasSorts;
  }

  get sortUdfs(): IUdf[] {
    return udfsSubscriptionService.subscriptionModel.getUdfsByDocumentType('employees');
  }

  get scheduleRuleContext(): IScheduleRuleContext | undefined {
    return this.state.scheduleRuleContext ?? this.searchScheduleRuleContext;
  }

  get searchScheduleRuleContext(): IScheduleRuleContext | undefined {
    return this.state.searchScheduleRuleContext;
  }

  get scheduleRuleContextSearchComponent() {
    return this.state.scheduleRuleContextSearchComponent;
  }

  get scheduleRuleQuery(): string | undefined {
    return this.state.scheduleRuleQuery;
  }

  get earnedDate(): UTC {
    return this.schedulingGridModel.earnedDate;
  }

  get viewOnly(): boolean {
    return mainPageModel.scheduleViewOnly;
  }

  get autoSchedulePropertyMap(): AutoSchedulePropertyMap {
    return this.getAutoSchedulePropertyMap();
  }

  get disableDate(): boolean {
    return this.state.disableDate;
  }

  get onSave() {
    return this.state.onSave;
  }

  get onDelete() {
    return this.state.onDelete;
  }

  protected async _load(obj?: ObjectType): Promise<void> {
    this.state.scheduleRuleContext = obj?.scheduleRuleContext;
    this.state.searchScheduleRuleContext = undefined;
    this.state.scheduleRuleId = obj?.scheduleRuleId;
    this.state.disableDate = obj?.disableDate ?? false;
    this.state.onSave = obj?.onSave;
    this.state.onDelete = obj?.onDelete;
    const earnedDate = obj?.earnedDate ?? UTC.now().startOfDay();
    this.state.scheduleRuleContextSearchComponent = ScheduleRuleContextSearch(
      this.onScheduleRuleContextSearchChange,
      () => this.earnedDate,
    );
    let scheduleRule: DeepPartial<IView> | undefined;
    if (this.scheduleRuleId) {
      scheduleRule = await this.getSchedulingRuleById(this.scheduleRuleId);
    }
    const sorts = scheduleRule?.options?.sorts as ScheduleSort;
    const filters = scheduleRule?.options?.filters;
    this.sortModel.sorts = sorts ?? {};
    await super._load(filters);
    this.state.scheduleRuleQuery = scheduleRule?.query;
    this.form.setFieldValue('scheduleRuleName', scheduleRule?.name);
    this.loadScheduleRuleCandidateList(!!this.state.scheduleRuleId, !!this.state.scheduleRuleId, earnedDate);
  }

  _reset(defaultObj?: any) {
    super._reset(defaultObj);
    this.state.scheduleRuleContext = undefined;
    this.state.searchScheduleRuleContext = undefined;
    this.state.scheduleRuleId = undefined;
    this.state.scheduleRuleContextSearchComponent = undefined;
    this.state.onSave = undefined;
    this.state.onDelete = undefined;
    this.state.filters = {};
    this.state.disableDate = false;
    this.sortModel.load();
  }

  onGridDateChange = (date?: UTC) => {
    this.updateScheduleRuleContextEarnedDate(date);
  };

  onScheduleRuleContextSearchChange = async (_evt: any, value: any) => {
    if (!value) {
      this.state.searchScheduleRuleContext = undefined;
    } else {
      const templateSchema = `
        id
        template {
          start
          end
          units
          reason { id category}
        }
      `;

      const templates = await DataQueries.getTemplatesByIds(templateSchema, [value.template?.id]);
      const template = templates?.[0] as ITemplate | undefined;

      if (!template) {
        this.state.searchScheduleRuleContext = undefined;
      } else {
        const group = { ...value.group, area: value.area };
        const job = { ...value.job, generator: value.generator };
        const timecards = template.template.map((t) =>
          timecardTemplateToTimecard({
            timecardTemplate: t,
            template: template,
            earnedDate: this.earnedDate,
            group: group,
            job: job,
            occupation: value.occupation,
          }),
        );
        this.state.searchScheduleRuleContext = getScheduleRuleContext(timecards);
      }
    }

    this.loadScheduleRuleCandidateList();
  };

  updateScheduleRuleContextEarnedDate(earnedDate?: UTC) {
    if (!earnedDate || !this.scheduleRuleContext) return;
    const prevEarnedDate = this.scheduleRuleContext.earnedDate!;
    this.scheduleRuleContext.earnedDate = earnedDate;
    if (!this.scheduleRuleContext.effective) return;
    const offset = earnedDate.subtractDate(prevEarnedDate);
    const newStart = this.scheduleRuleContext.effective.start.add(offset);
    const newEnd = this.scheduleRuleContext.effective.end.add(offset);
    this.scheduleRuleContext.effective = new TimestampRange(newStart, newEnd);
  }

  hasStateScheduleRuleContext() {
    return this.state.scheduleRuleContext;
  }

  async getSchedulingRuleById(id: string) {
    const schedulingRuleReturnSchema = `
      id
      name
      options
      query
    `;
    const result = await DataQueries.getViewsByIds(schedulingRuleReturnSchema, [id]);
    return result?.[0];
  }

  getPrioritySortItemsFn(): () => IPrioritySortMenuItem[] {
    return () => {
      return [
        { name: 'employee', title: 'Employee' },
        ...this.sortUdfs.map((udf: IUdf) => ({ name: udf.name, title: udf.displayName })),
      ];
    };
  }

  protected mapToSave(): ObjectType {
    return {
      filters: {
        custom: this.customFilterModel.mapToSave(),
      },
    };
  }

  protected async _save(obj: ObjectType): Promise<boolean> {
    this.state.filters = obj.filters;
    this.state.scheduleRuleQuery = this.createScheduleRuleQuery();
    const schedulingRule = (await this.saveSchedulingRule()) as IView | undefined;
    if (!schedulingRule) {
      return false;
    }
    return this.onSave?.({ id: schedulingRule.id, name: schedulingRule.name, query: schedulingRule.query }) ?? true;
  }

  getAutoSchedulePropertyMap() {
    const propertyMap = { ...this.propertyMap } as unknown as AutoSchedulePropertyMap;
    for (const key of Object.keys(propertyMap)) {
      const v = propertyMap[key] as ISchedulingPropertyMapField;
      propertyMap[key] = {
        field: v.field,
        internalField: v.internalField,
        index: v.index,
        category: v.category,
        intersectsFieldNames: v.intersectsFieldNames,
        udf: v.udf,
        mapToQuery: v.mapToQuery,
        customFilter: v.customFilter,
      };
    }

    return propertyMap;
  }

  createScheduleRuleQuery() {
    return getScheduleRuleQuery(this.autoSchedulePropertyMap, this.filters, this.sorts);
  }

  loadScheduleRuleCandidateList = async (initialLoad?: boolean, validateForm?: boolean, earnedDate?: UTC) => {
    if (validateForm) {
      this.form.validateForm();
    }
    if (!this.hasCustomFilterErrors()) {
      this.state.filters = this.mapToSave().filters;
      this.state.scheduleRuleQuery = this.createScheduleRuleQuery();
      await this.schedulingGridModel.load(
        this.scheduleRuleContext,
        this.scheduleRuleQuery,
        earnedDate,
        initialLoad,
        this.disableDate,
      );
    }
    return false;
  };

  hasInclusionFilter() {
    if (isNullOrUndefinedOrEmpty(this.scheduleRuleQuery)) {
      return false;
    }
    const queryObj = JSON.parse(this.scheduleRuleQuery);
    return queryObj.include?.jobs || queryObj.include?.timecards || queryObj.include?.expressions;
  }

  canSaveRule() {
    return !this.viewOnly && !this.hasErrors();
  }

  canDeleteRule() {
    return !this.viewOnly && this.scheduleRuleId;
  }

  async saveSchedulingRule() {
    if (!this.canSaveRule()) {
      return;
    }

    if (!this.hasInclusionFilter()) {
      notifications.error('Scheduling Rule must have at least one inclusion filter');
      return;
    }

    const schedulingRuleInput = {
      name: this.form.field.scheduleRuleName.value,
      options: { filters: this.filters, sorts: this.sorts },
      query: this.scheduleRuleQuery,
    } as ViewInsertInput | ViewUpdateInput;

    const returnSchema = `
      id
      name
      query
    `;
    let result: DeepPartial<IView>[] | undefined;

    if (this.scheduleRuleId) {
      (schedulingRuleInput as ViewUpdateInput)['id'] = this.scheduleRuleId;
      result = await DataMutations.updateViews(returnSchema, [schedulingRuleInput as unknown as ViewUpdateInput]);
    } else {
      schedulingRuleInput['type'] = 'schedulingRule';
      result = await DataMutations.insertViews(returnSchema, [schedulingRuleInput as ViewInsertInput]);
    }

    if (result?.[0]) {
      notifications.success(`Scheduling Rule ${this.scheduleRuleId ? 'Edited' : 'Created'}`);
      return result?.[0];
    }

    notifications.error(`Scheduling Rule ${this.scheduleRuleId ? 'Edit' : 'Creation'} Failed`);
    return;
  }

  deleteSchedulingRule = async () => {
    if (!this.canDeleteRule()) {
      return;
    }
    const result = await DataMutations.deleteViews([this.scheduleRuleId!]);
    if (result) {
      notifications.success('Scheduling Rule Deleted');
      this.close();
      this.onDelete?.({ id: this.scheduleRuleId! });
      this.reset();
    } else {
      notifications.error('Scheduling Rule Deletion Failed');
    }
  };
}

const scheduleRuleDialogModel = new ScheduleRuleDialogModel();
export default scheduleRuleDialogModel;
